"use strict";
// Copyright (c) 2023-2024 The Bitcoin developers
// Distributed under the MIT software license, see the accompanying
// file COPYING or http://www.opensource.org/licenses/mit-license.php.
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ALP_TOKEN_TYPES = exports.WsEndpoint = exports.PluginEndpoint = exports.LokadIdEndpoint = exports.TokenIdEndpoint = exports.ScriptEndpoint = exports.ChronikClient = exports.ConnectionStrategy = void 0;
exports.measureWebsocketLatency = measureWebsocketLatency;
exports.sortNodesByLatency = sortNodesByLatency;
const ecashaddrjs_1 = require("ecashaddrjs");
const isomorphic_ws_1 = __importDefault(require("isomorphic-ws"));
const proto = __importStar(require("../proto/chronik"));
const failoverProxy_1 = require("./failoverProxy");
const hex_1 = require("./hex");
const validation_1 = require("./validation");
var ConnectionStrategy;
(function (ConnectionStrategy) {
    ConnectionStrategy["ClosestFirst"] = "CLOSEST_FIRST";
    ConnectionStrategy["AsOrdered"] = "AS_ORDERED";
})(ConnectionStrategy || (exports.ConnectionStrategy = ConnectionStrategy = {}));
const WEBSOCKET_TIMEOUT_MS = 1000;
/**
 * Measures connection latency to a given WebSocket URL
 * @param {string} wsUrl WebSocket URL to test connection
 * @returns {Promise<number>} Returns latency in milliseconds, or Infinity if connection times out or fails
 */
async function measureWebsocketLatency(wsUrl) {
    return new Promise(resolve => {
        const timeoutFailure = setTimeout(() => {
            testWs.close();
            resolve(Infinity);
        }, WEBSOCKET_TIMEOUT_MS);
        const startTime = Date.now();
        const testWs = new isomorphic_ws_1.default(wsUrl);
        testWs.onerror = function () {
            testWs.close();
            clearTimeout(timeoutFailure);
            return resolve(Infinity);
        };
        testWs.onopen = function () {
            const latency = Date.now() - startTime;
            testWs.close();
            clearTimeout(timeoutFailure);
            return resolve(latency);
        };
    }).catch(() => {
        return Infinity;
    });
}
/**
 * Sort nodes by latency
 * @param {string[]} urls Array of URLs to sort
 * @returns {Promise<string[]>} Array of URLs sorted by latency
 */
async function sortNodesByLatency(urls) {
    // Convert URLs using appendWsUrls
    const endpoints = (0, failoverProxy_1.appendWsUrls)(urls);
    // Test latency of all endpoints in parallel
    const results = await Promise.all(endpoints.map(async (endpoint) => {
        const latency = await measureWebsocketLatency(endpoint.wsUrl);
        return {
            url: endpoint.url,
            latency: latency,
        };
    }));
    results.sort((a, b) => a.latency - b.latency);
    const sortedUrls = results.map(r => r.url);
    sortedUrls.forEach((url, idx) => {
        const result = results.find(r => r.url === url);
        console.log(result?.latency === Infinity
            ? `  ${idx + 1}. ${url} - latency: >${Math.round(WEBSOCKET_TIMEOUT_MS)}ms`
            : `  ${idx + 1}. ${url} - latency: ${Math.round(result?.latency || 0)}ms`);
    });
    return sortedUrls;
}
/**
 * Client to access an in-node Chronik instance.
 * Plain object, without any connections.
 */
class ChronikClient {
    /**
     * Create Chronik client instance with specified strategy
     *
     * @param {ConnectionStrategy} strategy Connection strategy
     * @param {string[]} urls Array of Chronik URLs
     * @returns {Promise<ChronikClient>} Client instance created with sorted URLs
     */
    static async useStrategy(strategy, urls) {
        let sortedUrls = [...urls];
        // If using ClosestFirst strategy, sort nodes by latency first
        if (strategy === ConnectionStrategy.ClosestFirst) {
            try {
                sortedUrls = await sortNodesByLatency(urls);
            }
            catch (error) {
                console.error('Error sorting nodes by latency:', error, 'Using original order:');
            }
        }
        return new ChronikClient(sortedUrls);
    }
    /**
     * Create a new client. This just creates an object, without any connections.
     *
     * @param {string[]} urls Array of valid urls. A valid url comes with schema and without a trailing slash.
     * e.g. `['https://chronik.e.cash', 'https://someotherchronikinstance.e.cash']`
     * The approach of accepting an array of urls as input is to ensure redundancy if the
     * first url encounters downtime.
     * @throws {error} throws error on invalid constructor inputs
     */
    constructor(urls) {
        // Instantiate FailoverProxy with the urls array
        this._proxyInterface = new failoverProxy_1.FailoverProxy(urls);
    }
    // For unit test verification
    proxyInterface() {
        return this._proxyInterface;
    }
    /**
     * Broadcasts the `rawTx` on the network.
     *
     * If `skipTokenChecks` is false, it will be checked that the tx doesn't burn
     * any tokens before broadcasting.
     *
     * NB this method DOES NOT wait for finalization
     */
    async broadcastTx(rawTx, skipTokenChecks = false) {
        return await this._broadcastTxRequest(rawTx, 0, skipTokenChecks);
    }
    /**
     * Broadcasts the `rawTxs` on the network, only if all of them are valid.
     *
     * If `skipTokenChecks` is false, it will be checked that the txs don't burn
     * any tokens before broadcasting.
     *
     * NB this method DOES NOT wait for finalization
     */
    async broadcastTxs(rawTxs, skipTokenChecks = false) {
        return await this._broadcastTxsRequest(rawTxs, 0, skipTokenChecks);
    }
    /**
     * Broadcasts the `rawTx` on the network.
     *
     * Wait for the tx to finalize by default. Allow user customization of finalizationTimeoutSecs.
     *
     * If `skipTokenChecks` is false, it will be checked that the tx doesn't burn
     * any tokens before broadcasting.
     */
    async broadcastAndFinalizeTx(rawTx, 
    // Default to 120s (chronik default)
    finalizationTimeoutSecs = 120, skipTokenChecks = false) {
        if (finalizationTimeoutSecs <= 0) {
            throw new Error('Use broadcastTx if you do not want to wait for finalization.');
        }
        return await this._broadcastTxRequest(rawTx, finalizationTimeoutSecs, skipTokenChecks);
    }
    /**
     * Broadcasts the `rawTxs` on the network, only if all of them are valid.
     *
     * Wait for the txs to finalize by default. Allow user customization of finalizationTimeoutSecs.
     *
     * If `skipTokenChecks` is false, it will be checked that the txs don't burn
     * any tokens before broadcasting.
     */
    async broadcastAndFinalizeTxs(rawTxs, 
    // Default to 120s (chronik default)
    finalizationTimeoutSecs = 120, skipTokenChecks = false) {
        if (finalizationTimeoutSecs <= 0) {
            throw new Error('Use broadcastTxs if you do not want to wait for finalization.');
        }
        return await this._broadcastTxsRequest(rawTxs, finalizationTimeoutSecs, skipTokenChecks);
    }
    /**
     * Private method to standardize method API calls for broadcasting txs for
     * both broadcastTx and broadcastAndFinalizeTx.
     */
    async _broadcastTxRequest(rawTx, finalizationTimeoutSecs, skipTokenChecks) {
        const request = proto.BroadcastTxRequest.encode({
            rawTx: typeof rawTx === 'string' ? (0, hex_1.fromHex)(rawTx) : rawTx,
            skipTokenChecks,
            finalizationTimeoutSecs: BigInt(finalizationTimeoutSecs),
        }).finish();
        const data = await this._proxyInterface.post('/broadcast-tx', request);
        const broadcastResponse = proto.BroadcastTxResponse.decode(data);
        return convertToBroadcastTxResponse(broadcastResponse);
    }
    /**
     * Private method to standardize method API calls for broadcasting txs for
     * both broadcastTxs and broadcastAndFinalizeTxs.
     */
    async _broadcastTxsRequest(rawTxs, finalizationTimeoutSecs, skipTokenChecks) {
        const request = proto.BroadcastTxsRequest.encode({
            rawTxs: rawTxs.map(rawTx => typeof rawTx === 'string' ? (0, hex_1.fromHex)(rawTx) : rawTx),
            finalizationTimeoutSecs: BigInt(finalizationTimeoutSecs),
            skipTokenChecks,
        }).finish();
        const data = await this._proxyInterface.post('/broadcast-txs', request);
        const broadcastResponse = proto.BroadcastTxsResponse.decode(data);
        return convertToBroadcastTxsResponse(broadcastResponse);
    }
    /**
     *  Validate a tx by rawtx
     *  This is a sort of preflight check before broadcasting a tx
     *  Allows us to
     *  - check before broadcast if a tx unintentionally burns tokens
     */
    async validateRawTx(rawTx) {
        // Validate input
        if (typeof rawTx !== 'string' && !(rawTx instanceof Uint8Array)) {
            // User has called validateRawTx with invalid input, no need to use chronik
            // to validate this rawTx
            throw new Error('rawTx must be a hex string or a Uint8Array');
        }
        const request = proto.RawTx.encode({
            rawTx: typeof rawTx === 'string' ? (0, hex_1.fromHex)(rawTx) : rawTx,
        }).finish();
        const data = await this._proxyInterface.post('/validate-tx', request);
        const validateResponse = proto.Tx.decode(data);
        return convertToTx(validateResponse);
    }
    /**
     * Fetch UTXOs for a batch of scripts.
     * Similar to utxos() but for multiple scripts in a single request.
     */
    async batchUtxos(scripts) {
        const request = proto.ScriptBatchUtxosRequest.encode({
            params: {
                scripts: scripts.map(s => ({
                    scriptType: s.scriptType,
                    payload: (0, hex_1.fromHex)(s.payload),
                })),
            },
        }).finish();
        const data = await this._proxyInterface.post('/script/batch/utxos', request);
        const response = proto.ScriptBatchUtxosResponse.decode(data);
        return response.rows.map(row => {
            if (row.utxos === undefined || row.script === undefined) {
                throw new Error('Invalid script batch utxos response');
            }
            return {
                script: scriptRefFromProto(row.script),
                utxos: decodeScriptUtxosFromProto(row.utxos),
            };
        });
    }
    /**
     * Per-script tx count, UTXO count, and the newest `Tx` in `history` order
     * (same as `script().history().txs[0]`), in a single POST.
     */
    async batchSummary(scripts) {
        const request = proto.ScriptBatchSummaryRequest.encode({
            params: {
                scripts: scripts.map(s => ({
                    scriptType: s.scriptType,
                    payload: (0, hex_1.fromHex)(s.payload),
                })),
            },
        }).finish();
        const data = await this._proxyInterface.post('/script/batch/summary', request);
        const response = proto.ScriptBatchSummaryResponse.decode(data);
        return response.rows.map(row => {
            if (row.script === undefined) {
                throw new Error('Invalid script batch summary response');
            }
            let latestTx;
            if (row.latestTx !== undefined) {
                latestTx = convertToTx(row.latestTx);
            }
            return {
                script: scriptRefFromProto(row.script),
                numTxs: row.numTxs,
                numUtxos: row.numUtxos,
                latestTx,
            };
        });
    }
    /** Fetch current info of the blockchain, such as tip hash and height. */
    async blockchainInfo() {
        const data = await this._proxyInterface.get(`/blockchain-info`);
        const blockchainInfo = proto.BlockchainInfo.decode(data);
        return convertToBlockchainInfo(blockchainInfo);
    }
    /** Fetch info about the current running chronik server */
    async chronikInfo() {
        const data = await this._proxyInterface.get(`/chronik-info`);
        const chronikServerInfo = proto.ChronikInfo.decode(data);
        return convertToChronikInfo(chronikServerInfo);
    }
    /** Fetch the block given hash or height. */
    async block(hashOrHeight) {
        const data = await this._proxyInterface.get(`/block/${hashOrHeight}`);
        const block = proto.Block.decode(data);
        return convertToBlock(block);
    }
    /** Fetch the tx history of a block given hash or height. */
    async blockTxs(hashOrHeight, page = 0, // Get the first page if unspecified
    pageSize = 25) {
        const data = await this._proxyInterface.get(`/block-txs/${hashOrHeight}?page=${page}&page_size=${pageSize}`);
        const blockTxs = proto.TxHistoryPage.decode(data);
        return convertToTxHistoryPage(blockTxs);
    }
    /**
     * Fetch unconfirmed transactions from the mempool.
     *
     * NB this endpoint is NOT paginated, even though it does return the TxHistoryPage shape
     */
    async unconfirmedTxs() {
        const data = await this._proxyInterface.get(`/unconfirmed-txs`);
        const unconfirmedTxs = proto.TxHistoryPage.decode(data);
        return convertToTxHistoryPage(unconfirmedTxs);
    }
    /**
     * Fetch block info of a range of blocks.
     * `startHeight` and `endHeight` are inclusive ranges.
     */
    async blocks(startHeight, endHeight) {
        const data = await this._proxyInterface.get(`/blocks/${startHeight}/${endHeight}`);
        const blocks = proto.Blocks.decode(data);
        return blocks.blocks.map(convertToBlockInfo);
    }
    /** Fetch token info and stats given the tokenId. */
    async token(tokenId) {
        const data = await this._proxyInterface.get(`/token/${tokenId}`);
        const tokenInfo = proto.TokenInfo.decode(data);
        return convertToTokenInfo(tokenInfo);
    }
    /** Fetch tx details given the txid. */
    async tx(txid) {
        const data = await this._proxyInterface.get(`/tx/${txid}`);
        const tx = proto.Tx.decode(data);
        return convertToTx(tx);
    }
    /** Fetch tx details given the txid. */
    async rawTx(txid) {
        const data = await this._proxyInterface.get(`/raw-tx/${txid}`);
        const rawTx = proto.RawTx.decode(data);
        return convertToRawTx(rawTx);
    }
    /** Create object that allows fetching info about a given token */
    tokenId(tokenId) {
        return new TokenIdEndpoint(this._proxyInterface, tokenId);
    }
    /** Create object that allows fetching info about a given lokadId */
    lokadId(lokadId) {
        return new LokadIdEndpoint(this._proxyInterface, lokadId);
    }
    /** Create object that allows fetching info about a given plugin */
    plugin(pluginName) {
        return new PluginEndpoint(this._proxyInterface, pluginName);
    }
    /** Create object that allows fetching script history or UTXOs. */
    script(scriptType, scriptPayload) {
        return new ScriptEndpoint(this._proxyInterface, scriptType, scriptPayload);
    }
    /** Create object that allows fetching script history or UTXOs by p2pkh or p2sh address */
    address(address) {
        const { type, hash } = (0, ecashaddrjs_1.decodeCashAddress)(address);
        return new ScriptEndpoint(this._proxyInterface, type, hash);
    }
    /** Open a WebSocket connection to listen for updates. */
    ws(config) {
        return new WsEndpoint(this._proxyInterface, config);
    }
}
exports.ChronikClient = ChronikClient;
/** Allows fetching script history and UTXOs. */
class ScriptEndpoint {
    constructor(proxyInterface, scriptType, scriptPayload) {
        this._proxyInterface = proxyInterface;
        this._scriptType = scriptType;
        this._scriptPayload = scriptPayload;
    }
    /**
     * Fetches the tx history of this script, in anti-chronological order.
     * This means it's ordered by first-seen first, i.e. TxHistoryPage.txs[0]
     * will be the most recent tx. If the tx hasn't been seen
     * by the indexer before, it's ordered by the block timestamp.
     * @param page Page index of the tx history.
     * @param pageSize Number of txs per page.
     */
    async history(page = 0, // Get the first page if unspecified
    pageSize = 25) {
        const data = await this._proxyInterface.get(`/script/${this._scriptType}/${this._scriptPayload}/history?page=${page}&page_size=${pageSize}`);
        const historyPage = proto.TxHistoryPage.decode(data);
        return {
            txs: historyPage.txs.map(convertToTx),
            numPages: historyPage.numPages,
            numTxs: historyPage.numTxs,
        };
    }
    /**
     * Fetches the confirmed tx history of this script, in the order they appear on the blockchain.
     * @param page Page index of the tx history.
     * @param pageSize Number of txs per page.
     */
    async confirmedTxs(page = 0, // Get the first page if unspecified
    pageSize = 25) {
        const data = await this._proxyInterface.get(`/script/${this._scriptType}/${this._scriptPayload}/confirmed-txs?page=${page}&page_size=${pageSize}`);
        const historyPage = proto.TxHistoryPage.decode(data);
        return {
            txs: historyPage.txs.map(convertToTx),
            numPages: historyPage.numPages,
            numTxs: historyPage.numTxs,
        };
    }
    /**
     * Fetches the unconfirmed tx history of this script, in chronological order.
     *
     * NB this endpoint is NOT paginated, even though it does return the TxHistoryPage shape
     */
    async unconfirmedTxs() {
        const data = await this._proxyInterface.get(`/script/${this._scriptType}/${this._scriptPayload}/unconfirmed-txs`);
        const historyPage = proto.TxHistoryPage.decode(data);
        return {
            txs: historyPage.txs.map(convertToTx),
            numPages: historyPage.numPages,
            numTxs: historyPage.numTxs,
        };
    }
    /**
     * Fetches the current UTXO set for this script.
     * It is grouped by output script, in case a script type can match multiple
     * different output scripts (e.g. Taproot on Lotus).
     */
    async utxos() {
        const data = await this._proxyInterface.get(`/script/${this._scriptType}/${this._scriptPayload}/utxos`);
        const scriptUtxos = proto.ScriptUtxos.decode(data);
        return decodeScriptUtxosFromProto(scriptUtxos);
    }
}
exports.ScriptEndpoint = ScriptEndpoint;
/** Allows fetching tokenId confirmedTxs, unconfirmedTxs, history, and UTXOs. */
class TokenIdEndpoint {
    constructor(proxyInterface, tokenId) {
        this._proxyInterface = proxyInterface;
        this._tokenId = tokenId;
    }
    /**
     * Fetches the tx history of this tokenId, in anti-chronological order.
     * @param page Page index of the tx history.
     * @param pageSize Number of txs per page.
     */
    async history(page = 0, // Get the first page if unspecified
    pageSize = 25) {
        const data = await this._proxyInterface.get(`/token-id/${this._tokenId}/history?page=${page}&page_size=${pageSize}`);
        const historyPage = proto.TxHistoryPage.decode(data);
        return {
            txs: historyPage.txs.map(convertToTx),
            numPages: historyPage.numPages,
            numTxs: historyPage.numTxs,
        };
    }
    /**
     * Fetches the confirmed tx history of this tokenId, in the order they appear on the blockchain.
     * @param page Page index of the tx history.
     * @param pageSize Number of txs per page.
     */
    async confirmedTxs(page = 0, // Get the first page if unspecified
    pageSize = 25) {
        const data = await this._proxyInterface.get(`/token-id/${this._tokenId}/confirmed-txs?page=${page}&page_size=${pageSize}`);
        const historyPage = proto.TxHistoryPage.decode(data);
        return {
            txs: historyPage.txs.map(convertToTx),
            numPages: historyPage.numPages,
            numTxs: historyPage.numTxs,
        };
    }
    /**
     * Fetches the unconfirmed tx history of this tokenId, in chronological order.
     *
     * NB this endpoint is NOT paginated, even though it does return the TxHistoryPage shape
     */
    async unconfirmedTxs() {
        const data = await this._proxyInterface.get(`/token-id/${this._tokenId}/unconfirmed-txs`);
        const historyPage = proto.TxHistoryPage.decode(data);
        return {
            txs: historyPage.txs.map(convertToTx),
            numPages: historyPage.numPages,
            numTxs: historyPage.numTxs,
        };
    }
    /**
     * Fetches the current UTXO set for this tokenId.
     */
    async utxos() {
        const data = await this._proxyInterface.get(`/token-id/${this._tokenId}/utxos`);
        const utxos = proto.Utxos.decode(data);
        return {
            tokenId: this._tokenId,
            utxos: utxos.utxos.map(convertToUtxo),
        };
    }
}
exports.TokenIdEndpoint = TokenIdEndpoint;
/** Allows fetching lokadId confirmedTxs, unconfirmedTxs, and history. */
class LokadIdEndpoint {
    constructor(proxyInterface, lokadId) {
        this._proxyInterface = proxyInterface;
        this._lokadId = lokadId;
    }
    /**
     * Fetches the tx history of this tokenId, in anti-chronological order.
     * @param page Page index of the tx history.
     * @param pageSize Number of txs per page.
     */
    async history(page = 0, // Get the first page if unspecified
    pageSize = 25) {
        const data = await this._proxyInterface.get(`/lokad-id/${this._lokadId}/history?page=${page}&page_size=${pageSize}`);
        const historyPage = proto.TxHistoryPage.decode(data);
        return {
            txs: historyPage.txs.map(convertToTx),
            numPages: historyPage.numPages,
            numTxs: historyPage.numTxs,
        };
    }
    /**
     * Fetches the confirmed tx history of this tokenId, in the order they appear on the blockchain.
     * @param page Page index of the tx history.
     * @param pageSize Number of txs per page.
     */
    async confirmedTxs(page = 0, // Get the first page if unspecified
    pageSize = 25) {
        const data = await this._proxyInterface.get(`/lokad-id/${this._lokadId}/confirmed-txs?page=${page}&page_size=${pageSize}`);
        const historyPage = proto.TxHistoryPage.decode(data);
        return {
            txs: historyPage.txs.map(convertToTx),
            numPages: historyPage.numPages,
            numTxs: historyPage.numTxs,
        };
    }
    /**
     * Fetches the unconfirmed tx history of this tokenId, in chronological order.
     *
     * NB this endpoint is NOT paginated, even though it does return the TxHistoryPage shape
     */
    async unconfirmedTxs() {
        const data = await this._proxyInterface.get(`/lokad-id/${this._lokadId}/unconfirmed-txs`);
        const historyPage = proto.TxHistoryPage.decode(data);
        return {
            txs: historyPage.txs.map(convertToTx),
            numPages: historyPage.numPages,
            numTxs: historyPage.numTxs,
        };
    }
}
exports.LokadIdEndpoint = LokadIdEndpoint;
/** Allows fetching plugin UTXOs. */
class PluginEndpoint {
    constructor(proxyInterface, pluginName) {
        this._proxyInterface = proxyInterface;
        this._pluginName = pluginName;
    }
    /**
     * Fetches the current UTXO set for this plugin group.
     */
    async utxos(groupHex) {
        const data = await this._proxyInterface.get(`/plugin/${this._pluginName}/${groupHex}/utxos`);
        const utxos = proto.Utxos.decode(data);
        return {
            pluginName: this._pluginName,
            groupHex,
            utxos: utxos.utxos.map(convertToUtxo),
        };
    }
    /**
     * Fetches groups of this plugin.
     */
    async groups(prefixHex, startHex, pageSize) {
        const query = new URLSearchParams();
        if (prefixHex !== undefined) {
            query.set('prefix', prefixHex);
        }
        if (startHex !== undefined) {
            query.set('start', startHex);
        }
        if (pageSize !== undefined) {
            query.set('page_size', pageSize.toString());
        }
        const data = await this._proxyInterface.get(`/plugin/${this._pluginName}/groups?${query.toString()}`);
        const groups = proto.PluginGroups.decode(data);
        return convertToPluginGroups(groups);
    }
    /**
     * Fetches the tx history of this groupHex for this plugin, in anti-chronological order.
     * @param groupHex group as a lowercase hex string
     * @param page Page index of the tx history.
     * @param pageSize Number of txs per page.
     */
    async history(groupHex, page = 0, // Get the first page if unspecified
    pageSize = 25) {
        const data = await this._proxyInterface.get(`/plugin/${this._pluginName}/${groupHex}/history?page=${page}&page_size=${pageSize}`);
        const historyPage = proto.TxHistoryPage.decode(data);
        return {
            txs: historyPage.txs.map(convertToTx),
            numPages: historyPage.numPages,
            numTxs: historyPage.numTxs,
        };
    }
    /**
     * Fetches the confirmed tx history of this groupHex for this plugin, in the order they appear on the blockchain.
     * @param groupHex group as a lowercase hex string
     * @param page Page index of the tx history.
     * @param pageSize Number of txs per page.
     */
    async confirmedTxs(groupHex, page = 0, // Get the first page if unspecified
    pageSize = 25) {
        const data = await this._proxyInterface.get(`/plugin/${this._pluginName}/${groupHex}/confirmed-txs?page=${page}&page_size=${pageSize}`);
        const historyPage = proto.TxHistoryPage.decode(data);
        return {
            txs: historyPage.txs.map(convertToTx),
            numPages: historyPage.numPages,
            numTxs: historyPage.numTxs,
        };
    }
    /**
     * Fetches the unconfirmed tx history of this groupHex for this plugin, in chronological order.
     * @param groupHex group as a lowercase hex string
     *
     * NB this endpoint is NOT paginated, even though it does return the TxHistoryPage shape
     */
    async unconfirmedTxs(groupHex) {
        const data = await this._proxyInterface.get(`/plugin/${this._pluginName}/${groupHex}/unconfirmed-txs`);
        const historyPage = proto.TxHistoryPage.decode(data);
        return {
            txs: historyPage.txs.map(convertToTx),
            numPages: historyPage.numPages,
            numTxs: historyPage.numTxs,
        };
    }
}
exports.PluginEndpoint = PluginEndpoint;
/** WebSocket connection to Chronik. */
class WsEndpoint {
    constructor(proxyInterface, config) {
        this.onMessage = config.onMessage;
        this.onConnect = config.onConnect;
        this.onReconnect = config.onReconnect;
        this.onEnd = config.onEnd;
        this.autoReconnect =
            config.autoReconnect !== undefined ? config.autoReconnect : true;
        this.manuallyClosed = false;
        this.subs = {
            scripts: [],
            tokens: [],
            txids: [],
            lokadIds: [],
            plugins: [],
            blocks: false,
            txs: false,
        };
        this._proxyInterface = proxyInterface;
    }
    /** Wait for the WebSocket to be connected. */
    async waitForOpen() {
        await this._proxyInterface.connectWs(this);
        await this.connected;
    }
    /**
     * Subscribe to block messages
     */
    subscribeToBlocks() {
        this.subs.blocks = true;
        if (this.ws?.readyState === isomorphic_ws_1.default.OPEN) {
            this._subUnsubBlocks(false);
        }
    }
    /**
     * Unsubscribe from block messages
     */
    unsubscribeFromBlocks() {
        this.subs.blocks = false;
        if (this.ws?.readyState === isomorphic_ws_1.default.OPEN) {
            this._subUnsubBlocks(true);
        }
    }
    /**
     * Subscribe to all tx messages
     */
    subscribeToTxs() {
        this.subs.txs = true;
        if (this.ws?.readyState === isomorphic_ws_1.default.OPEN) {
            this._subUnsubTxs(false);
        }
    }
    /**
     * Unsubscribe from all tx messages
     */
    unsubscribeFromTxs() {
        this.subs.txs = false;
        if (this.ws?.readyState === isomorphic_ws_1.default.OPEN) {
            this._subUnsubTxs(true);
        }
    }
    /**
     * Subscribe to the given script type and payload.
     * For "p2pkh", `scriptPayload` is the 20 byte public key hash.
     */
    subscribeToScript(type, payload) {
        // Build sub according to chronik expected type
        const subscription = {
            scriptType: type,
            payload,
        };
        // We do not want to add invalid subs to ws.subs
        const scriptSubscriptionValidationCheck = (0, validation_1.isValidWsSubscription)(subscription);
        if (scriptSubscriptionValidationCheck !== true) {
            // isValidWsSubscription returns string error msg if the sub is invalid
            throw new Error(scriptSubscriptionValidationCheck);
        }
        this.subs.scripts.push(subscription);
        if (this.ws?.readyState === isomorphic_ws_1.default.OPEN) {
            this._subUnsubScript(false, subscription);
        }
    }
    /** Unsubscribe from the given script type and payload. */
    unsubscribeFromScript(type, payload) {
        // Build sub according to chronik expected type
        const subscription = {
            scriptType: type,
            payload,
        };
        // Find the requested unsub script and remove it
        const unsubIndex = this.subs.scripts.findIndex(sub => sub.scriptType === type && sub.payload === payload);
        if (unsubIndex === -1) {
            // If we cannot find this subscription in this.subs, throw an error
            // We do not want an app developer thinking they have unsubscribed from something
            throw new Error(`No existing sub at ${type}, ${payload}`);
        }
        // Remove the requested subscription from this.subs
        this.subs.scripts.splice(unsubIndex, 1);
        if (this.ws?.readyState === isomorphic_ws_1.default.OPEN) {
            this._subUnsubScript(true, subscription);
        }
    }
    /**
     * Subscribe to an address
     * Method can be used for p2pkh or p2sh addresses
     */
    subscribeToAddress(address) {
        // Get type and hash
        const { type, hash } = (0, ecashaddrjs_1.decodeCashAddress)(address);
        // Subscribe to script
        this.subscribeToScript(type, hash);
    }
    /** Unsubscribe from the given address */
    unsubscribeFromAddress(address) {
        // Get type and hash
        const { type, hash } = (0, ecashaddrjs_1.decodeCashAddress)(address);
        // Unsubscribe from script
        this.unsubscribeFromScript(type, hash);
    }
    /** Subscribe to a lokadId */
    subscribeToLokadId(lokadId) {
        (0, validation_1.verifyLokadId)(lokadId);
        // Update ws.subs to include this lokadId
        this.subs.lokadIds.push(lokadId);
        if (this.ws?.readyState === isomorphic_ws_1.default.OPEN) {
            // Send subscribe msg to chronik server
            this._subUnsubLokadId(false, lokadId);
        }
    }
    /** Unsubscribe from the given lokadId */
    unsubscribeFromLokadId(lokadId) {
        // Find the requested unsub lokadId and remove it
        const unsubIndex = this.subs.lokadIds.findIndex(thisLokadId => thisLokadId === lokadId);
        if (unsubIndex === -1) {
            // If we cannot find this subscription in this.subs.lokadIds, throw an error
            // We do not want an app developer thinking they have unsubscribed from something if no action happened
            throw new Error(`No existing sub at lokadId "${lokadId}"`);
        }
        // Remove the requested lokadId subscription from this.subs.lokadIds
        this.subs.lokadIds.splice(unsubIndex, 1);
        if (this.ws?.readyState === isomorphic_ws_1.default.OPEN) {
            // Send unsubscribe msg to chronik server
            this._subUnsubLokadId(true, lokadId);
        }
    }
    /** Subscribe to a tokenId */
    subscribeToTokenId(tokenId) {
        (0, validation_1.verifyTokenId)(tokenId);
        // Update ws.subs to include this tokenId
        this.subs.tokens.push(tokenId);
        if (this.ws?.readyState === isomorphic_ws_1.default.OPEN) {
            // Send subscribe msg to chronik server
            this._subUnsubToken(false, tokenId);
        }
    }
    /** Unsubscribe from the given tokenId */
    unsubscribeFromTokenId(tokenId) {
        // Find the requested unsub tokenId and remove it
        const unsubIndex = this.subs.tokens.findIndex(thisTokenId => thisTokenId === tokenId);
        if (unsubIndex === -1) {
            // If we cannot find this subscription in this.subs.tokens, throw an error
            // We do not want an app developer thinking they have unsubscribed from something if no action happened
            throw new Error(`No existing sub at tokenId "${tokenId}"`);
        }
        // Remove the requested tokenId subscription from this.subs.tokens
        this.subs.tokens.splice(unsubIndex, 1);
        if (this.ws?.readyState === isomorphic_ws_1.default.OPEN) {
            // Send unsubscribe msg to chronik server
            this._subUnsubToken(true, tokenId);
        }
    }
    /** Subscribe to a plugin */
    subscribeToPlugin(pluginName, group) {
        // Build sub according to chronik expected type
        const subscription = {
            pluginName,
            group,
        };
        (0, validation_1.verifyPluginSubscription)(subscription);
        // Update ws.subs to include this plugin
        this.subs.plugins.push(subscription);
        if (this.ws?.readyState === isomorphic_ws_1.default.OPEN) {
            // Send subscribe msg to chronik server
            this._subUnsubPlugin(false, subscription);
        }
    }
    /** Unsubscribe from the given plugin */
    unsubscribeFromPlugin(pluginName, group) {
        // Find the requested unsub script and remove it
        const unsubIndex = this.subs.plugins.findIndex(sub => sub.pluginName === pluginName && sub.group === group);
        if (unsubIndex === -1) {
            // If we cannot find this subscription in this.subs.plugins, throw an error
            // We do not want an app developer thinking they have unsubscribed from something
            throw new Error(`No existing sub at pluginName="${pluginName}", group="${group}"`);
        }
        // Remove the requested subscription from this.subs.plugins
        this.subs.plugins.splice(unsubIndex, 1);
        if (this.ws?.readyState === isomorphic_ws_1.default.OPEN) {
            // Send unsubscribe msg to chronik server
            this._subUnsubPlugin(true, {
                pluginName,
                group,
            });
        }
    }
    /** Subscribe to a txid */
    subscribeToTxid(txid) {
        (0, validation_1.verifyTxid)(txid);
        // Update ws.subs to include this txid
        this.subs.txids.push(txid);
        if (this.ws?.readyState === isomorphic_ws_1.default.OPEN) {
            // Send subscribe msg to chronik server
            this._subUnsubTxid(false, txid);
        }
    }
    /** Unsubscribe from the given txid */
    unsubscribeFromTxid(txid) {
        // Find the requested unsub txid and remove it
        const unsubIndex = this.subs.txids.findIndex(thisTxid => thisTxid === txid);
        if (unsubIndex === -1) {
            // If we cannot find this subscription in this.subs.txids, throw an error
            // We do not want an app developer thinking they have unsubscribed from something if no action happened
            throw new Error(`No existing sub to txid "${txid}"`);
        }
        // Remove the requested txid subscription from this.subs.txids
        this.subs.txids.splice(unsubIndex, 1);
        if (this.ws?.readyState === isomorphic_ws_1.default.OPEN) {
            // Send unsubscribe msg to chronik server
            this._subUnsubTxid(true, txid);
        }
    }
    /**
     * Close the WebSocket connection and prevent any future reconnection
     * attempts.
     */
    close() {
        this.manuallyClosed = true;
        this.ws?.close();
    }
    /**
     * Pause the WebSocket connection by disabling auto-reconnect and closing
     * the connection. Useful when the app is backgrounded to save resources.
     *
     * Because we cannot predict the behavior of mobile operating systems handling
     * websocket connections, it is better for the app developer to manually handle.
     *
     * We provide standard methods to accomplish this.
     */
    pause() {
        this.autoReconnect = false;
        if (this.ws) {
            // Note we DO NOT set manuallyClosed to true here, unlike the
            // public close() method, because we plan to re-open the websocket,
            // and we do not want to cycle through the failover proxy servers.
            this.ws.close();
        }
    }
    /**
     * Resume the WebSocket connection by re-enabling auto-reconnect and
     * reconnecting if the connection is closed. Useful when the app comes
     * to foreground.
     */
    async resume() {
        // Don't resume if websocket was manually closed
        if (this.manuallyClosed) {
            return;
        }
        this.autoReconnect = true;
        // If the connection is closed, reconnect
        if (!this.ws ||
            this.ws.readyState === isomorphic_ws_1.default.CLOSING ||
            this.ws.readyState === isomorphic_ws_1.default.CLOSED) {
            // connectWs onopen re-sends this.subs
            await this._proxyInterface.connectWs(this);
            await this.connected;
        }
    }
    /**
     * Re-send every current subscription over the open socket.
     *
     * @internal Called from FailoverProxy.connectWs onopen. Public only
     * so failoverProxy.ts can call it.
     */
    _resubscribeAll() {
        if (this.ws?.readyState !== isomorphic_ws_1.default.OPEN) {
            return;
        }
        this.subs.scripts.forEach(sub => this._subUnsubScript(false, sub));
        this.subs.lokadIds.forEach(lokadId => this._subUnsubLokadId(false, lokadId));
        this.subs.tokens.forEach(tokenId => this._subUnsubToken(false, tokenId));
        this.subs.plugins.forEach(plugin => this._subUnsubPlugin(false, plugin));
        this.subs.txids.forEach(txid => this._subUnsubTxid(false, txid));
        if (this.subs.blocks === true) {
            this._subUnsubBlocks(false);
        }
        if (this.subs.txs === true) {
            this._subUnsubTxs(false);
        }
    }
    _subUnsubBlocks(isUnsub) {
        // Blocks subscription is empty object
        const BLOCKS_SUBSCRIPTION = {};
        const encodedSubscription = proto.WsSub.encode({
            isUnsub,
            blocks: BLOCKS_SUBSCRIPTION,
        }).finish();
        if (this.ws === undefined) {
            throw new Error('Invalid state; _ws is undefined');
        }
        this.ws.send(encodedSubscription);
    }
    _subUnsubTxs(isUnsub) {
        // Txs subscription is empty object
        const TXS_SUBSCRIPTION = {};
        const encodedSubscription = proto.WsSub.encode({
            isUnsub,
            txs: TXS_SUBSCRIPTION,
        }).finish();
        if (this.ws === undefined) {
            throw new Error('Invalid state; _ws is undefined');
        }
        this.ws.send(encodedSubscription);
    }
    _subUnsubScript(isUnsub, subscription) {
        // If this subscription is to an address, leave the 'blocks' key undefined
        const encodedSubscription = proto.WsSub.encode({
            isUnsub,
            script: {
                scriptType: subscription.scriptType,
                payload: (0, hex_1.fromHex)(subscription.payload),
            },
        }).finish();
        if (this.ws === undefined) {
            throw new Error('Invalid state; _ws is undefined');
        }
        this.ws.send(encodedSubscription);
    }
    _subUnsubLokadId(isUnsub, lokadId) {
        const encodedSubscription = proto.WsSub.encode({
            isUnsub,
            lokadId: {
                // User input for lokadId is string
                // Chronik expects bytes
                lokadId: (0, hex_1.fromHex)(lokadId),
            },
        }).finish();
        if (this.ws === undefined) {
            throw new Error('Invalid state; _ws is undefined');
        }
        this.ws.send(encodedSubscription);
    }
    _subUnsubToken(isUnsub, tokenId) {
        const encodedSubscription = proto.WsSub.encode({
            isUnsub,
            tokenId: {
                tokenId: tokenId,
            },
        }).finish();
        if (this.ws === undefined) {
            throw new Error('Invalid state; _ws is undefined');
        }
        this.ws.send(encodedSubscription);
    }
    _subUnsubTxid(isUnsub, txid) {
        const encodedSubscription = proto.WsSub.encode({
            isUnsub,
            txid: {
                txid: txid,
            },
        }).finish();
        if (this.ws === undefined) {
            throw new Error('Invalid state; _ws is undefined');
        }
        this.ws.send(encodedSubscription);
    }
    _subUnsubPlugin(isUnsub, plugin) {
        const encodedSubscription = proto.WsSub.encode({
            isUnsub,
            plugin: {
                pluginName: plugin.pluginName,
                // User input for plugin group is string
                // Chronik expects bytes
                group: (0, hex_1.fromHex)(plugin.group),
            },
        }).finish();
        if (this.ws === undefined) {
            throw new Error('Invalid state; _ws is undefined');
        }
        this.ws.send(encodedSubscription);
    }
    async handleMsg(wsMsg) {
        if (typeof this.onMessage === 'undefined') {
            return;
        }
        const data = typeof window === 'undefined'
            ? // NodeJS
                wsMsg.data
            : // Browser
                new Uint8Array(await wsMsg.data.arrayBuffer());
        const msg = proto.WsMsg.decode(data);
        if (typeof msg.error !== 'undefined') {
            this.onMessage({ type: 'Error', ...msg.error });
        }
        else if (typeof msg.block !== 'undefined') {
            const msgBlock = {
                type: 'Block',
                msgType: convertToBlockMsgType(msg.block.msgType),
                blockHash: (0, hex_1.toHexRev)(msg.block.blockHash),
                blockHeight: msg.block.blockHeight,
                blockTimestamp: Number(msg.block.blockTimestamp),
            };
            if (typeof msg.block.coinbaseData !== 'undefined') {
                msgBlock.coinbaseData = convertToCoinbaseData(msg.block.coinbaseData);
            }
            this.onMessage(msgBlock);
        }
        else if (typeof msg.tx !== 'undefined') {
            const txMsg = {
                type: 'Tx',
                msgType: convertToTxMsgType(msg.tx.msgType),
                txid: (0, hex_1.toHexRev)(msg.tx.txid),
            };
            if (typeof msg.tx.finalizationReason !== 'undefined') {
                txMsg.finalizationReasonType =
                    convertToTxFinalizationReasonType(msg.tx.finalizationReason.finalizationType);
            }
            this.onMessage(txMsg);
        }
        else {
            console.log('Silently ignored unknown Chronik message:', msg);
        }
    }
}
exports.WsEndpoint = WsEndpoint;
function convertToBlockchainInfo(blockchainInfo) {
    return {
        tipHash: (0, hex_1.toHexRev)(blockchainInfo.tipHash),
        tipHeight: blockchainInfo.tipHeight,
    };
}
function convertToChronikInfo(chronikInfo) {
    if (chronikInfo.version === undefined) {
        throw new Error('chronikInfo has no version');
    }
    return {
        version: chronikInfo.version.length !== 0 ? chronikInfo.version : '',
    };
}
function convertToBlock(block) {
    if (block.blockInfo === undefined) {
        throw new Error('Block has no blockInfo');
    }
    return {
        blockInfo: convertToBlockInfo(block.blockInfo),
    };
}
function convertToTxHistoryPage(blockTxs) {
    const { txs, numPages, numTxs } = blockTxs;
    const convertedTxs = txs.map(convertToTx);
    return {
        txs: convertedTxs,
        numPages,
        numTxs,
    };
}
function convertToBlockInfo(block) {
    return {
        ...block,
        hash: (0, hex_1.toHexRev)(block.hash),
        prevHash: (0, hex_1.toHexRev)(block.prevHash),
        timestamp: Number(block.timestamp),
        blockSize: Number(block.blockSize),
        numTxs: Number(block.numTxs),
        numInputs: Number(block.numInputs),
        numOutputs: Number(block.numOutputs),
        sumInputSats: block.sumInputSats,
        sumCoinbaseOutputSats: block.sumCoinbaseOutputSats,
        sumNormalOutputSats: block.sumNormalOutputSats,
        sumBurnedSats: block.sumBurnedSats,
    };
}
function convertToTx(tx) {
    const convertedTx = {
        txid: (0, hex_1.toHexRev)(tx.txid),
        version: tx.version,
        inputs: tx.inputs.map(convertToTxInput),
        outputs: tx.outputs.map(convertToTxOutput),
        lockTime: tx.lockTime,
        timeFirstSeen: Number(tx.timeFirstSeen),
        size: tx.size,
        isCoinbase: tx.isCoinbase,
        tokenEntries: tx.tokenEntries.map(convertToTokenEntry),
        tokenFailedParsings: tx.tokenFailedParsings.map(convertToTokenFailedParsing),
        tokenStatus: convertToTokenStatus(tx.tokenStatus),
        isFinal: tx.isFinal,
    };
    if (typeof tx.block !== 'undefined') {
        // Only include block if the tx is in a block
        convertedTx.block = convertToBlockMeta(tx.block);
    }
    return convertedTx;
}
function convertToTxInput(input) {
    if (input.prevOut === undefined) {
        throw new Error('Invalid proto, no prevOut');
    }
    const txInput = {
        prevOut: {
            txid: (0, hex_1.toHexRev)(input.prevOut.txid),
            outIdx: input.prevOut.outIdx,
        },
        inputScript: (0, hex_1.toHex)(input.inputScript),
        sats: input.sats,
        sequenceNo: input.sequenceNo,
    };
    if (typeof input.token !== 'undefined') {
        // We only return a token key if we have token data for this input
        txInput.token = convertToToken(input.token);
    }
    if (Object.keys(input.plugins).length > 0) {
        // We only return a plugins key if we have plugins
        txInput.plugins = convertToPluginEntries(input.plugins);
    }
    if (typeof input.outputScript !== 'undefined' &&
        input.outputScript.length > 0) {
        // Coinbase tx inputs do not have an outputScript
        txInput.outputScript = (0, hex_1.toHex)(input.outputScript);
    }
    return txInput;
}
function convertToTxOutput(output) {
    const txOutput = {
        sats: BigInt(output.sats),
        outputScript: (0, hex_1.toHex)(output.outputScript),
    };
    if (Object.keys(output.plugins).length > 0) {
        // We only return a plugins key if we have plugins
        txOutput.plugins = convertToPluginEntries(output.plugins);
    }
    if (typeof output.token !== 'undefined') {
        // We only return a token key if we have token data for this input
        txOutput.token = convertToToken(output.token);
    }
    if (typeof output.spentBy !== 'undefined') {
        // We only return a spentBy key if this output has been spent
        txOutput.spentBy = {
            txid: (0, hex_1.toHexRev)(output.spentBy.txid),
            outIdx: output.spentBy.inputIdx,
        };
    }
    return txOutput;
}
function convertToPluginEntries(plugins) {
    const pluginEntries = {};
    for (const [pluginName, plugin] of Object.entries(plugins)) {
        if (typeof pluginName === 'undefined') {
            continue;
        }
        const { groups, data } = plugin;
        pluginEntries[pluginName] = {
            groups: groups.map(hex_1.toHex),
            data: data.map(hex_1.toHex),
        };
    }
    return pluginEntries;
}
function convertToPluginGroups(protoPluginGroups) {
    const { groups } = protoPluginGroups;
    return {
        groups: groups.map(group => ({ group: (0, hex_1.toHex)(group.group) })),
        nextStart: (0, hex_1.toHex)(protoPluginGroups.nextStart),
    };
}
function convertToBlockMeta(block) {
    return {
        height: block.height,
        hash: (0, hex_1.toHexRev)(block.hash),
        timestamp: Number(block.timestamp),
    };
}
function convertToRawTx(rawTx) {
    return {
        rawTx: (0, hex_1.toHex)(rawTx.rawTx),
    };
}
function decodeScriptUtxosFromProto(scriptUtxos) {
    return {
        outputScript: (0, hex_1.toHex)(scriptUtxos.script),
        utxos: scriptUtxos.utxos.map(convertToScriptUtxo),
    };
}
function scriptRefFromProto(ref) {
    return {
        scriptType: ref.scriptType,
        payload: (0, hex_1.toHex)(ref.payload),
    };
}
function convertToScriptUtxo(utxo) {
    if (utxo.outpoint === undefined) {
        throw new Error('UTXO outpoint is undefined');
    }
    const convertedScriptUtxo = {
        outpoint: {
            txid: (0, hex_1.toHexRev)(utxo.outpoint.txid),
            outIdx: utxo.outpoint.outIdx,
        },
        blockHeight: utxo.blockHeight,
        isCoinbase: utxo.isCoinbase,
        sats: BigInt(utxo.sats),
        isFinal: utxo.isFinal,
    };
    if (typeof utxo.token !== 'undefined') {
        // We only return a token key if we have token data for this input
        convertedScriptUtxo.token = convertToToken(utxo.token);
    }
    if (Object.keys(utxo.plugins).length > 0) {
        // We only return a plugins key if we have plugins
        convertedScriptUtxo.plugins = convertToPluginEntries(utxo.plugins);
    }
    return convertedScriptUtxo;
}
function convertToUtxo(utxo) {
    if (utxo.outpoint === undefined) {
        throw new Error('UTXO outpoint is undefined');
    }
    const convertedUtxo = {
        outpoint: {
            txid: (0, hex_1.toHexRev)(utxo.outpoint.txid),
            outIdx: utxo.outpoint.outIdx,
        },
        blockHeight: utxo.blockHeight,
        isCoinbase: utxo.isCoinbase,
        script: (0, hex_1.toHex)(utxo.script),
        sats: BigInt(utxo.sats),
        isFinal: utxo.isFinal,
    };
    if (typeof utxo.token !== 'undefined') {
        // We only return a token key if we have token data for this input
        convertedUtxo.token = convertToToken(utxo.token);
    }
    if (Object.keys(utxo.plugins).length > 0) {
        // We only return a plugins key if we have plugins
        convertedUtxo.plugins = convertToPluginEntries(utxo.plugins);
    }
    return convertedUtxo;
}
function convertToTokenEntry(tokenEntry) {
    if (typeof tokenEntry.tokenType === 'undefined') {
        // Not expected to ever happen
        throw new Error(`chronik returned undefined tokenEntry.tokenType for tokenId "${tokenEntry.tokenId}"`);
    }
    const returnObj = {
        tokenId: tokenEntry.tokenId,
        tokenType: convertToTokenType(tokenEntry.tokenType),
        txType: convertToTokenTxType(tokenEntry.txType),
        isInvalid: tokenEntry.isInvalid,
        burnSummary: tokenEntry.burnSummary,
        failedColorings: tokenEntry.failedColorings,
        actualBurnAtoms: BigInt(tokenEntry.actualBurnAtoms),
        intentionalBurnAtoms: tokenEntry.intentionalBurnAtoms,
        burnsMintBatons: tokenEntry.burnsMintBatons,
    };
    if (tokenEntry.groupTokenId !== '') {
        // Only include groupTokenId if it is not empty
        returnObj.groupTokenId = tokenEntry.groupTokenId;
    }
    return returnObj;
}
function convertToTokenFailedParsing(tokenFailedParsing) {
    return {
        pushdataIdx: tokenFailedParsing.pushdataIdx,
        bytes: (0, hex_1.toHex)(tokenFailedParsing.bytes),
        error: tokenFailedParsing.error,
    };
}
function convertToTokenType(tokenType) {
    if (typeof tokenType.alp !== 'undefined') {
        return {
            protocol: 'ALP',
            type: convertToAlpTokenType(tokenType.alp),
            number: tokenType.alp,
        };
    }
    if (typeof tokenType.slp !== 'undefined') {
        return {
            protocol: 'SLP',
            type: convertToSlpTokenType(tokenType.slp),
            number: tokenType.slp,
        };
    }
    // In case the Chronik instance supports a protocol this client doesn't
    return {
        protocol: 'UNKNOWN',
        type: 'UNKNOWN',
        number: 0,
    };
}
function convertToSlpTokenType(msgType) {
    const slpTokenType = proto.slpTokenTypeToJSON(msgType);
    if (isSlpTokenType(slpTokenType)) {
        return slpTokenType;
    }
    return 'SLP_TOKEN_TYPE_UNKNOWN';
}
function isSlpTokenType(msgType) {
    return SLP_TOKEN_TYPES.includes(msgType);
}
function convertToAlpTokenType(msgType) {
    const alpTokenType = proto.alpTokenTypeToJSON(msgType);
    if (isAlpTokenType(alpTokenType)) {
        return alpTokenType;
    }
    return 'ALP_TOKEN_TYPE_UNKNOWN';
}
function isAlpTokenType(msgType) {
    return exports.ALP_TOKEN_TYPES.includes(msgType);
}
function convertToTokenStatus(msgType) {
    const tokenStatus = proto.tokenStatusToJSON(msgType);
    if (isTokenStatus(tokenStatus)) {
        return tokenStatus;
    }
    return 'TOKEN_STATUS_UNKNOWN';
}
function isTokenStatus(msgType) {
    return TOKEN_STATUS_TYPES.includes(msgType);
}
function convertToTokenTxType(msgType) {
    const tokenTxType = proto.tokenTxTypeToJSON(msgType);
    if (isTokenTxType(tokenTxType)) {
        return tokenTxType;
    }
    return 'UNKNOWN';
}
function isTokenTxType(msgType) {
    return TOKEN_TX_TYPE_TYPES.includes(msgType);
}
function convertToToken(token) {
    if (typeof token.tokenType === 'undefined') {
        // Not expected to ever happen
        throw new Error(`chronik returned undefined token.tokenType for tokenId "${token.tokenId}"`);
    }
    const convertedToken = {
        tokenId: token.tokenId,
        tokenType: convertToTokenType(token.tokenType),
        atoms: BigInt(token.atoms),
        isMintBaton: token.isMintBaton,
    };
    // We do not bother including entryIdx for utxos, where it is always -1
    if (token.entryIdx !== -1) {
        convertedToken.entryIdx = token.entryIdx;
    }
    return convertedToken;
}
function convertToBlockMsgType(msgType) {
    const blockMsgType = proto.blockMsgTypeToJSON(msgType);
    if (isBlockMsgType(blockMsgType)) {
        return blockMsgType;
    }
    return 'UNRECOGNIZED';
}
function isBlockMsgType(msgType) {
    return BLK_MSG_TYPES.includes(msgType);
}
function convertToTxMsgType(msgType) {
    const txMsgType = proto.txMsgTypeToJSON(msgType);
    if (isTxMsgType(txMsgType)) {
        return txMsgType;
    }
    return 'UNRECOGNIZED';
}
function isTxMsgType(msgType) {
    return TX_MSG_TYPES.includes(msgType);
}
// Add converter and type guards for tx finalization reason
function convertToTxFinalizationReasonType(reason) {
    const reasonStr = proto.txFinalizationReasonTypeToJSON(reason);
    if (isTxFinalizationReasonType(reasonStr)) {
        return reasonStr;
    }
    return 'UNRECOGNIZED';
}
function isTxFinalizationReasonType(reason) {
    return TX_FINALIZATION_REASON_TYPES.includes(reason);
}
function convertToTokenInfo(tokenInfo) {
    if (typeof tokenInfo.tokenType === 'undefined') {
        // Not expected to ever happen
        throw new Error(`chronik returned undefined tokenInfo.tokenType for tokenId "${tokenInfo.tokenId}"`);
    }
    if (typeof tokenInfo.genesisInfo === 'undefined') {
        // Not expected to ever happen
        throw new Error(`chronik returned undefined tokenInfo.genesisInfo for tokenId "${tokenInfo.tokenId}"`);
    }
    // We use tokenType to get the correct shape of genesisInfo
    const tokenType = convertToTokenType(tokenInfo.tokenType);
    const returnedTokenInfo = {
        tokenId: tokenInfo.tokenId,
        tokenType,
        timeFirstSeen: Number(tokenInfo.timeFirstSeen),
        genesisInfo: convertToGenesisInfo(tokenInfo.genesisInfo, tokenType),
    };
    if (typeof tokenInfo.block !== 'undefined') {
        // Only include block if the tx is in a block
        returnedTokenInfo.block = convertToBlockMeta(tokenInfo.block);
    }
    return returnedTokenInfo;
}
function convertToGenesisInfo(genesisInfo, tokenType) {
    const decoder = new TextDecoder();
    const returnedGenesisInfo = {
        tokenTicker: decoder.decode(genesisInfo.tokenTicker),
        tokenName: decoder.decode(genesisInfo.tokenName),
        url: decoder.decode(genesisInfo.url),
        decimals: genesisInfo.decimals,
    };
    // Add ALP fields for ALP types only
    if (tokenType.protocol === 'ALP') {
        returnedGenesisInfo.data = (0, hex_1.toHex)(genesisInfo.data);
        returnedGenesisInfo.authPubkey = (0, hex_1.toHex)(genesisInfo.authPubkey);
    }
    // Add mintVaultHash for SLP Mint Vault only
    if (tokenType.type === 'SLP_TOKEN_TYPE_MINT_VAULT') {
        returnedGenesisInfo.mintVaultScripthash = (0, hex_1.toHex)(genesisInfo.mintVaultScripthash);
    }
    // Add url for SLP only
    if (tokenType.protocol === 'SLP') {
        returnedGenesisInfo.hash = (0, hex_1.toHex)(genesisInfo.hash);
    }
    return returnedGenesisInfo;
}
function convertToCoinbaseData(coinbaseData) {
    const returnedCoinbaseData = {
        scriptsig: (0, hex_1.toHex)(coinbaseData.coinbaseScriptsig),
        outputs: coinbaseData.coinbaseOutputs.map(convertToTxOutput),
    };
    return returnedCoinbaseData;
}
function convertToBroadcastTxResponse(broadcastResponse) {
    return {
        txid: (0, hex_1.toHexRev)(broadcastResponse.txid),
    };
}
function convertToBroadcastTxsResponse(broadcastResponse) {
    return {
        txids: broadcastResponse.txids.map(hex_1.toHexRev),
    };
}
exports.ALP_TOKEN_TYPES = [
    'ALP_TOKEN_TYPE_STANDARD',
    'ALP_TOKEN_TYPE_UNKNOWN',
];
const SLP_TOKEN_TYPES = [
    'SLP_TOKEN_TYPE_FUNGIBLE',
    'SLP_TOKEN_TYPE_MINT_VAULT',
    'SLP_TOKEN_TYPE_NFT1_GROUP',
    'SLP_TOKEN_TYPE_NFT1_CHILD',
    'SLP_TOKEN_TYPE_UNKNOWN',
];
const TOKEN_STATUS_TYPES = [
    'TOKEN_STATUS_NON_TOKEN',
    'TOKEN_STATUS_NORMAL',
    'TOKEN_STATUS_NOT_NORMAL',
    'TOKEN_STATUS_UNKNOWN',
];
const TOKEN_TX_TYPE_TYPES = [
    'NONE',
    'UNKNOWN',
    'GENESIS',
    'SEND',
    'MINT',
    'BURN',
];
const BLK_MSG_TYPES = [
    'BLK_CONNECTED',
    'BLK_DISCONNECTED',
    'BLK_FINALIZED',
    'BLK_INVALIDATED',
    'UNRECOGNIZED',
];
const TX_MSG_TYPES = [
    'TX_ADDED_TO_MEMPOOL',
    'TX_REMOVED_FROM_MEMPOOL',
    'TX_CONFIRMED',
    'TX_FINALIZED',
    'TX_INVALIDATED',
    'UNRECOGNIZED',
];
const TX_FINALIZATION_REASON_TYPES = [
    'TX_FINALIZATION_REASON_POST_CONSENSUS',
    'TX_FINALIZATION_REASON_PRE_CONSENSUS',
    'UNRECOGNIZED',
];
//# sourceMappingURL=ChronikClient.js.map